import React, { useEffect, useState } from 'react';
import { VoucherWithCodes, CreateVoucherRequest, UpdateVoucherRequest } from '../../../../../types/voucher.types';
import Button from '../../../../components/common/Button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Editor } from 'primereact/editor';
import { RadioButton } from 'primereact/radiobutton';
import { Divider } from 'primereact/divider';
import './PublicVoucherForm.css';

interface PublicVoucherFormProps {
    visible: boolean;
    voucher?: VoucherWithCodes | null;
    onHide: () => void;
    onSubmit: (data: CreateVoucherRequest | UpdateVoucherRequest) => Promise<void>;
    loading?: boolean;
}

interface FormData {
    title: string;
    description: string;
    discount_type: 'value' | 'percentage';
    discount_value?: number;
    discount_percentage?: number;
    max_discount?: number;
    start_date: Date | null;
    end_date: Date | null;
    max_usage: number;
    minimum_amount?: number;
    voucher_type_vehicle: 'car' | 'motorbike' | 'both';
}

const PublicVoucherForm: React.FC<PublicVoucherFormProps> = ({
    visible,
    voucher,
    onHide,
    onSubmit,
    loading = false
}) => {
    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        discount_type: 'percentage',
        discount_percentage: 10,
        max_discount: undefined,
        start_date: new Date(),
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        max_usage: 1,
        voucher_type_vehicle: 'both'
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showHelp, setShowHelp] = useState<boolean>(false);

    // Vehicle type options
    const vehicleTypeOptions = [
        { label: 'Tất cả phương tiện', value: 'both' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' }
    ];

    // Initialize form when voucher changes
    useEffect(() => {
        if (voucher) {
            // Edit mode - populate form with voucher data
            setFormData({
                title: voucher.title || '',
                description: voucher.description || '',
                discount_type: voucher.discount_value ? 'value' : 'percentage',
                discount_value: voucher.discount_value || undefined,
                discount_percentage: voucher.discount_percentage || undefined,
                max_discount: voucher.max_discount || undefined,
                start_date: new Date(voucher.start_date),
                end_date: new Date(voucher.end_date),
                max_usage: voucher.max_usage,
                minimum_amount: voucher.minimum_amount || undefined,
                voucher_type_vehicle: voucher.voucher_type_vehicle || 'both'
            });
        } else {
            // Create mode - reset to defaults
            setFormData({
                title: '',
                description: '',
                discount_type: 'percentage',
                discount_percentage: 10,
                max_discount: undefined,
                start_date: new Date(),
                end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                max_usage: 1,
                voucher_type_vehicle: 'both'
            });
        }
        setErrors({});
    }, [voucher, visible]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Validate title
        if (!formData.title || formData.title.trim().length === 0) {
            newErrors.title = 'Tiêu đề voucher là bắt buộc';
        } else if (formData.title.trim().length < 3) {
            newErrors.title = 'Tiêu đề voucher phải có ít nhất 3 ký tự';
        } else if (formData.title.trim().length > 100) {
            newErrors.title = 'Tiêu đề voucher không được quá 100 ký tự';
        }

        // Validate description
        if (!formData.description || formData.description.trim().length === 0) {
            newErrors.description = 'Mô tả voucher là bắt buộc';
        } else if (formData.description.trim().length < 10) {
            newErrors.description = 'Mô tả voucher phải có ít nhất 10 ký tự';
        }

        // Validate discount
        if (formData.discount_type === 'value') {
            if (!formData.discount_value || formData.discount_value <= 0) {
                newErrors.discount_value = 'Giá trị giảm giá phải lớn hơn 0';
            }
        } else {
            if (!formData.discount_percentage || formData.discount_percentage <= 0 || formData.discount_percentage > 100) {
                newErrors.discount_percentage = 'Phần trăm giảm giá phải từ 1% đến 100%';
            }
            // Validate max_discount when using percentage discount
            if (!formData.max_discount || formData.max_discount <= 0) {
                newErrors.max_discount = 'Số tiền giảm tối đa là bắt buộc khi chọn giảm theo phần trăm';
            }
        }

        // Validate dates
        if (!formData.start_date) {
            newErrors.start_date = 'Ngày bắt đầu là bắt buộc';
        }
        if (!formData.end_date) {
            newErrors.end_date = 'Ngày kết thúc là bắt buộc';
        }
        if (formData.start_date && formData.end_date && formData.start_date >= formData.end_date) {
            newErrors.end_date = 'Ngày kết thúc phải sau ngày bắt đầu';
        }

        // Validate usage limits
        if (formData.max_usage < 1) {
            newErrors.max_usage = 'Số lần sử dụng tối đa phải ít nhất là 1';
        }

        // Validate minimum amount
        if (formData.minimum_amount && formData.minimum_amount < 0) {
            newErrors.minimum_amount = 'Số tiền tối thiểu không được âm';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const submitData: CreateVoucherRequest | UpdateVoucherRequest = {
                voucher_type: 'public',
                title: formData.title.trim(),
                description: formData.description.trim(),
                discount_value: formData.discount_type === 'value' ? formData.discount_value : undefined,
                discount_percentage: formData.discount_type === 'percentage' ? formData.discount_percentage : undefined,
                max_discount: formData.discount_type === 'percentage' ? formData.max_discount : undefined,
                start_date: formData.start_date!.toISOString(),
                end_date: formData.end_date!.toISOString(),
                max_usage: formData.max_usage,
                minimum_amount: formData.minimum_amount,
                voucher_type_vehicle: formData.voucher_type_vehicle,
                // Public vouchers specific - these should not be set
                point_cost: undefined, // Public vouchers don't cost points
                code: undefined, // Public vouchers don't have codes
                total_redemption_limit: undefined, // Public vouchers don't have total limits
                user_redemption_limit: undefined, // Public vouchers don't have user limits
                ...(voucher && { id: voucher.id })
            };

            console.log('PublicVoucherForm - Submit data:', submitData);
            console.log('PublicVoucherForm - max_discount:', submitData.max_discount);
            console.log('PublicVoucherForm - minimum_amount:', submitData.minimum_amount);

            await onSubmit(submitData);
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const dialogHeader = voucher ? 'Chỉnh sửa voucher chung' : 'Tạo voucher chung mới';

    // Custom header template with help button
    const headerTemplate = () => {
        return (
            <div className="dialog-header-custom">
                <span className="dialog-title">{dialogHeader}</span>
                <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setShowHelp(true)}
                    className="help-button"
                >
                    <i className="pi pi-question-circle"></i>
                </Button>
            </div>
        );
    };

    return (
        <>
            <Dialog
                header={headerTemplate}
                visible={visible}
                onHide={onHide}
                modal
                className="public-voucher-form-dialog"
                style={{ width: '600px' }}
                footer={
                    <div className="form-footer">
                        <Button
                            variant="secondary"
                            onClick={onHide}
                            disabled={loading}
                        >
                            Hủy
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={loading}
                        >
                            {voucher ? 'Cập nhật' : 'Tạo voucher'}
                        </Button>
                    </div>
                }
            >
                <div className="public-voucher-form">
                    {/* Basic Information */}
                    <div className="form-section">
                        <h3>Thông tin cơ bản</h3>

                        <div className="form-field">
                            <label>Tiêu đề voucher *</label>
                            <InputText
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="VD: Giảm giá đặc biệt cho mọi chuyến đi"
                                className={errors.title ? 'p-invalid' : ''}
                                maxLength={100}
                            />
                            {errors.title && (
                                <small className="error-text">{errors.title}</small>
                            )}
                            <small className="form-help">
                                Tiêu đề voucher dễ hiểu, hấp dẫn (3-100 ký tự)
                            </small>
                        </div>

                        <div className="form-field">
                            <label>Mô tả chi tiết *</label>
                            <Editor
                                value={formData.description}
                                onTextChange={(e) => setFormData(prev => ({ ...prev, description: e.htmlValue || '' }))}
                                style={{ height: '500px' }}
                                className={errors.description ? 'p-invalid' : ''}
                                placeholder="Mô tả chi tiết về voucher, điều kiện sử dụng, quyền lợi..."
                            />
                            {errors.description && (
                                <small className="error-text">{errors.description}</small>
                            )}

                        </div>
                    </div>

                    <Divider />

                    {/* Discount Configuration */}
                    <div className="form-section">
                        <h3>Cấu hình giảm giá</h3>

                        <div className="discount-type-group">
                            <div className="radio-option">
                                <RadioButton
                                    inputId="discount-percentage"
                                    name="discount_type" value="percentage"
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        discount_type: e.value,
                                        discount_value: undefined,
                                        max_discount: prev.max_discount || 50000
                                    }))}
                                    checked={formData.discount_type === 'percentage'}
                                />
                                <label htmlFor="discount-percentage" className="ml-2">Giảm theo phần trăm (%)</label>
                            </div>
                            <div className="radio-option">
                                <RadioButton
                                    inputId="discount-value"
                                    name="discount_type"
                                    value="value"
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        discount_type: e.value,
                                        discount_percentage: undefined,
                                        max_discount: undefined
                                    }))}
                                    checked={formData.discount_type === 'value'}
                                />
                                <label htmlFor="discount-value" className="ml-2">Giảm theo số tiền cố định (VNĐ)</label>
                            </div>
                        </div>

                        {formData.discount_type === 'percentage' ? (
                            <>
                                <div className="form-field">
                                    <label>Phần trăm giảm giá *</label>
                                    <InputNumber
                                        value={formData.discount_percentage}
                                        onValueChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.value || 0 }))}
                                        suffix="%"
                                        min={1}
                                        max={100}
                                        className={errors.discount_percentage ? 'p-invalid' : ''}
                                    />
                                    {errors.discount_percentage && (
                                        <small className="error-text">{errors.discount_percentage}</small>
                                    )}
                                </div>
                                <div className="form-field">
                                    <label>Số tiền giảm tối đa *</label>
                                    <InputNumber
                                        value={formData.max_discount}
                                        onValueChange={(e) => setFormData(prev => ({ ...prev, max_discount: e.value || 0 }))}
                                        mode="currency"
                                        currency="VND"
                                        locale="vi-VN"
                                        min={1000}
                                        placeholder="Nhập số tiền giảm tối đa"
                                        className={errors.max_discount ? 'p-invalid' : ''}
                                    />
                                    {errors.max_discount && (
                                        <small className="error-text">{errors.max_discount}</small>
                                    )}
                                    <small className="field-help">
                                        Khi giảm theo phần trăm, cần giới hạn số tiền giảm tối đa để kiểm soát chi phí
                                    </small>
                                </div>
                            </>
                        ) : (
                            <div className="form-field">
                                <label>Số tiền giảm giá *</label>
                                <InputNumber
                                    value={formData.discount_value}
                                    onValueChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.value || 0 }))}
                                    mode="currency"
                                    currency="VND"
                                    locale="vi-VN"
                                    min={1000}
                                    className={errors.discount_value ? 'p-invalid' : ''}
                                />
                                {errors.discount_value && (
                                    <small className="error-text">{errors.discount_value}</small>
                                )}
                            </div>
                        )}

                        <div className="form-field">
                            <label>Số tiền tối thiểu để áp dụng</label>
                            <InputNumber
                                value={formData.minimum_amount}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, minimum_amount: e.value || undefined }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                placeholder="Không giới hạn"
                                className={errors.minimum_amount ? 'p-invalid' : ''}
                            />
                            {errors.minimum_amount && (
                                <small className="error-text">{errors.minimum_amount}</small>
                            )}
                        </div>
                    </div>

                    <Divider />

                    {/* Date Configuration */}
                    <div className="form-section">
                        <h3>Thời gian áp dụng</h3>

                        <div className="date-range">
                            <div className="form-field">
                                <label>Ngày bắt đầu *</label>
                                <Calendar
                                    value={formData.start_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.value as Date }))}
                                    showTime
                                    hourFormat="24"
                                    dateFormat="dd/mm/yy"
                                    className={errors.start_date ? 'p-invalid' : ''}
                                />
                                {errors.start_date && (
                                    <small className="error-text">{errors.start_date}</small>
                                )}
                            </div>

                            <div className="form-field">
                                <label>Ngày kết thúc *</label>
                                <Calendar
                                    value={formData.end_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.value as Date }))}
                                    showTime
                                    hourFormat="24"
                                    dateFormat="dd/mm/yy"
                                    className={errors.end_date ? 'p-invalid' : ''}
                                />
                                {errors.end_date && (
                                    <small className="error-text">{errors.end_date}</small>
                                )}
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Usage Limits */}
                    <div className="form-section">
                        <h3>Giới hạn sử dụng</h3>

                        <div className="form-field">
                            <label>Số lần sử dụng tối đa mỗi người *</label>
                            <InputNumber
                                value={formData.max_usage}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, max_usage: e.value || 1 }))}
                                min={1}
                                className={errors.max_usage ? 'p-invalid' : ''}
                            />
                            {errors.max_usage && (
                                <small className="error-text">{errors.max_usage}</small>
                            )}
                            <small className="field-help">
                                Voucher chung không cần giới hạn tổng số lần sử dụng hoặc theo người dùng
                            </small>
                        </div>
                    </div>

                    <Divider />

                    {/* Vehicle Type */}
                    <div className="form-section">
                        <h3>Phương tiện áp dụng</h3>

                        <div className="form-field">
                            <label>Loại phương tiện *</label>
                            <Dropdown
                                value={formData.voucher_type_vehicle}
                                onChange={(e) => setFormData(prev => ({ ...prev, voucher_type_vehicle: e.value }))}
                                options={vehicleTypeOptions}
                                placeholder="Chọn loại phương tiện"
                            />
                        </div>
                    </div>

                    {/* Information Notes */}
                    <div className="form-notes">
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Voucher chung được áp dụng tự động cho tất cả người dùng mà không cần mã code</span>
                        </div>
                        <div className="note-item">
                            <i className="pi pi-info-circle"></i>
                            <span>Voucher chung không tốn điểm tích lũy của người dùng</span>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Help Dialog */}
            <Dialog
                header="Hướng dẫn Voucher Chung"
                visible={showHelp}
                onHide={() => setShowHelp(false)}
                modal
                className="voucher-help-dialog"
                style={{ width: '700px' }}
            >
                <div className="help-content">
                    <div className="help-section">
                        <h3>Voucher Chung là gì?</h3>
                        <p>Voucher chung là loại voucher công khai, áp dụng cho tất cả người dùng mà không cần mã code cụ thể (ví dụ: giảm giá 10% cho mọi đơn hàng trong sự kiện).</p>
                    </div>

                    <div className="help-section">
                        <h3>Các thông tin cần thiết:</h3>
                        <ul className="help-list">
                            <li><strong>Tiêu đề voucher:</strong> Tên voucher hiển thị cho người dùng, ngắn gọn và hấp dẫn (3-100 ký tự).</li>
                            <li><strong>Mô tả chi tiết:</strong> Thông tin chi tiết về voucher, quyền lợi, điều kiện áp dụng (10-1000 ký tự).</li>
                            <li><strong>Loại giảm giá:</strong> Chọn giảm theo phần trăm (%) hoặc số tiền cố định (VNĐ). Chỉ được chọn một trong hai.</li>
                            <li><strong>Giảm giá tối đa:</strong> Khi chọn giảm theo phần trăm, bắt buộc phải có số tiền giảm tối đa.</li>
                            <li><strong>Thời gian hiệu lực:</strong> Ngày bắt đầu và kết thúc voucher.</li>
                            <li><strong>Số lần sử dụng tối đa mỗi người:</strong> Giới hạn số lần một người dùng có thể sử dụng voucher này (mặc định 1).</li>
                            <li><strong>Số tiền tối thiểu:</strong> (Tùy chọn) Giá trị đơn hàng tối thiểu để có thể áp dụng voucher.</li>
                            <li><strong>Loại phương tiện:</strong> Voucher áp dụng cho ô tô, xe máy, hoặc cả hai.</li>
                        </ul>
                    </div>

                    <div className="help-section">
                        <h3>Đặc điểm của Voucher Chung:</h3>
                        <ul className="help-list">
                            <li><strong>Không cần mã code:</strong> Voucher tự động áp dụng cho tất cả người dùng đủ điều kiện.</li>
                            <li><strong>Không tốn điểm:</strong> Người dùng không cần dùng điểm tích lũy để đổi voucher.</li>
                            <li><strong>Không giới hạn tổng số lần:</strong> Voucher chung không cần giới hạn tổng số lần sử dụng toàn hệ thống.</li>
                            <li><strong>Công khai:</strong> Hiển thị cho tất cả người dùng trong ứng dụng.</li>
                            <li><strong>Tự động áp dụng:</strong> Hệ thống sẽ tự động kiểm tra và áp dụng voucher phù hợp nhất cho đơn hàng.</li>
                        </ul>
                    </div>

                    <div className="help-section">
                        <h3>Lưu ý quan trọng:</h3>
                        <div className="help-notes">
                            <div className="help-note warning">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>Chỉ được chọn một trong hai: giảm theo phần trăm HOẶC giảm theo số tiền cố định.</span>
                            </div>
                            <div className="help-note warning">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>Khi chọn giảm theo phần trăm, bắt buộc phải nhập số tiền giảm tối đa để kiểm soát chi phí.</span>
                            </div>
                            <div className="help-note info">
                                <i className="pi pi-info-circle"></i>
                                <span>Voucher chung sẽ tự động hiển thị trong danh sách voucher của người dùng khi còn hiệu lực.</span>
                            </div>
                            <div className="help-note info">
                                <i className="pi pi-info-circle"></i>
                                <span>Không cần thiết giới hạn tổng số lần sử dụng do tính chất công khai của voucher này.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default PublicVoucherForm;
